import {
  Box,
  Button,
  Card,
  Divider,
  InlineGrid,
  Page,
  Text,
  TextField,
  BlockStack,
  useBreakpoints,
  ContextualSaveBar,
  Checkbox,
  FormLayout,
  Form,
} from '@shopify/polaris';
import { useField, useForm } from '@shopify/react-form';
import { useNotification } from 'context/NotificationContext';
import { useSettings } from 'context/SettingsContext';
import __ from 'languages/index';
import { useUpdateSetting } from 'queries/setting.query';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

export default function Embed() {
  const { addNotification } = useNotification();

  const { smUp } = useBreakpoints();

  const { settings: entities } = useSettings();
  const { mutateAsync: updateEntity, error, isSuccess: updateSuccess, isPending: updating } = useUpdateSetting();

  const useFields = {
    /** Goong */
    _maps_key: useField<string>({ value: entities?._maps_key ?? '', validates: [] }),
    _maps_uri: useField<string>({ value: entities?._maps_uri ?? '', validates: [] }),

    /** matbao */
    _matbao_taikhoan: useField<string>({ value: entities?._matbao_taikhoan ?? '', validates: [] }),
    _matbao_matkhau: useField<string>({ value: entities?._matbao_matkhau ?? '', validates: [] }),
    _matbao_api_uri: useField<string>({ value: entities?._matbao_api_uri ?? '', validates: [] }),
    _matbao_mst: useField<string>({ value: entities?._matbao_mst ?? '', validates: [] }),
    _matbao_khmshdon: useField<string>({ value: entities?._matbao_khmshdon ?? '', validates: [] }),
    _matbao_khhdon: useField<string>({ value: entities?._matbao_khhdon ?? '', validates: [] }),
    _matbao_thuesuat: useField<string>({ value: entities?._matbao_thuesuat ?? 5, validates: [] }),
    __auto_create_draft_invoice_before_midnight: useField<string>({
      value: entities?.__auto_create_draft_invoice_before_midnight ?? '0',
      validates: [],
    }),
    _auto_sign_invoice_before_midnight: useField<string>({ value: entities?._auto_sign_invoice_before_midnight ?? '0', validates: [] }),

    _mapbox_key: useField<string>({ value: entities?._mapbox_key ?? '', validates: [] }),
    _mapbox_public_key: useField<string>({ value: entities?._mapbox_public_key ?? '', validates: [] }),
    _auto_sync_trip_manifest: useField<string>({ value: entities?._auto_sync_trip_manifest ?? 'false', validates: [] }),

    /** OpenAI */
    _openai_api_key: useField<string>({ value: entities?._openai_api_key ?? '', validates: [] }),
    _openai_model: useField<string>({ value: entities?._openai_model ?? '', validates: [] }),
    _openai_system_instruction: useField<string>({ value: entities?._openai_system_instruction ?? '', validates: [] }),
  };

  const {
    fields,
    submit,
    submitting,
    dirty,
    reset: resetForm,
    submitErrors,
    makeClean,
  } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await updateEntity({
          _matbao_taikhoan: values._matbao_taikhoan,
          _matbao_matkhau: values._matbao_matkhau,
          _matbao_api_uri: values._matbao_api_uri,
          _matbao_mst: values._matbao_mst,
          _matbao_khmshdon: values._matbao_khmshdon,
          _matbao_khhdon: values._matbao_khhdon,
          _matbao_thuesuat: values._matbao_thuesuat,
          __auto_create_draft_invoice_before_midnight: values.__auto_create_draft_invoice_before_midnight,
          _auto_sign_invoice_before_midnight: values._auto_sign_invoice_before_midnight,

          _maps_key: values._maps_key,
          _maps_uri: values._maps_uri,
          _mapbox_key: values._mapbox_key,
          _mapbox_public_key: values._mapbox_public_key,
          _auto_sync_trip_manifest: values._auto_sync_trip_manifest,

          _openai_api_key: values._openai_api_key,
          _openai_model: values._openai_model,
          _openai_system_instruction: values._openai_system_instruction,
        });
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });

  /**
   * Notification
   */

  useEffect(() => {
    if (error) {
      addNotification('error', error.message);
    }
  }, [error]);

  useEffect(() => {
    if (updateSuccess) addNotification('info', __('updated_successfully'));
  }, [updateSuccess]);

  return (
    <>
      <Helmet>
        <title>Cài đặt mã nhúng và kết nối</title>
      </Helmet>
      <Page title="Mã nhúng và kết nối">
        <BlockStack gap={{ xs: '800', sm: '400' }}>
          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Mã nhúng cho web
                </Text>
                <Text as="p" variant="bodyMd">
                  Mã Passport.js là mã quan trọng, giúp các chức năng bên dưới hoạt động trơn tru và bảo vệ bạn khỏi Spambot tấn công.
                </Text>
              </BlockStack>
            </Box>
            <Card>
              <Text as="p" variant="bodySm" tone="subdued">
                Nhúng mã sau đây lên landing page của bạn, trước thẻ <code>&lt;/head&gt;</code> và để ở mọi trang.
              </Text>
              <TextField
                label=" "
                value={`<script crossorigin="anonymous" type="application/javascript" src="${process.env.REACT_APP_PUBLIC_URL}/passport.js"></script>`}
                onChange={() => {}}
                multiline={2}
                autoComplete="off"
              />
              <br />
              <Text as="p" variant="bodySm" tone="subdued">
                Nhúng mã sau đây lên landing page của bạn, trước thẻ <code>&lt;/body&gt;</code> và để ở trang bạn muốn tracking người dùng.
              </Text>
              <TextField
                label=" "
                value={`<script>
        var backend_url = '${process.env.REACT_APP_BACKEND_URL}/activity/web_activity'; 
        var requestee_option = {};
    </script>
    <script crossorigin="anonymous" defer="defer" type="application/javascript" src="${process.env.REACT_APP_PUBLIC_URL}/requestee.js"></script>`}
                onChange={() => {}}
                multiline={2}
                autoComplete="off"
              />
              <br />
              <Text as="p" variant="bodySm" tone="subdued">
                Nếu bạn muốn định nghĩa trang đích để tracking tốt hơn, bạn có thể đặt dữ liệu vào biến <br />
                <code>requestee_option.activity_target = 'YOUR_DEFINITION'; // định nghĩa tên trang đích </code> <br />
                <code>requestee_option.activity_referrer = 'YOUR_DEFINITION'; // định nghĩa trang giới thiệu </code> <br />
                Khi đó bạn có thể dễ dàng định nghĩa và lọc được người dùng truy cập một cách dễ dàng.
              </Text>
            </Card>
          </InlineGrid>

          {smUp ? <Divider /> : null}

          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  OpenAI API
                </Text>
                <Text as="p" variant="bodyMd">
                  Cấu hình kết nối và hướng dẫn mặc định cho ChatGPT.
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <BlockStack gap="400">
                <TextField
                  label="OpenAI API Key"
                  autoComplete="off"
                  {...fields._openai_api_key}
                  helpText="Khóa bí mật dùng để xác thực các yêu cầu gửi tới OpenAI API. Đăng ký tại đây: https://platform.openai.com/api-keys"
                />
                <TextField
                  label="Model"
                  autoComplete="off"
                  placeholder="Nhập model ID"
                  {...fields._openai_model}
                  helpText="Model ID được sử dụng khi gửi yêu cầu."
                />
                <TextField
                  label="System Instruction"
                  autoComplete="off"
                  multiline={4}
                  {...fields._openai_system_instruction}
                  helpText="Chỉ dẫn mặc định quy định cách trợ lý phản hồi."
                />
              </BlockStack>
            </Card>
          </InlineGrid>

          {smUp ? <Divider /> : null}

          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Contact form
                </Text>
                <Text as="p" variant="bodyMd">
                  Form liên hệ để kết nối giữa khách hàng và doanh nghiệp thông qua website hoặc app.
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <BlockStack gap={'200'}>
                <Text as="h3" variant="headingSm">
                  Dành cho website
                </Text>
                <Text as="p">Bạn thực hiện truy vấn như sau:</Text>
                <pre>
                  {`URL: ${process.env.REACT_APP_BACKEND_URL}/contactform
METHOD: POST
BODY: {
    contactform_title: String,
    contactform_content: String,
    contactform_email: String,
    // hoặc
    contactform_name: String
}`}
                </pre>
                <Text as="p">
                  {' '}
                  Để có thể lưu một Contact Form trên website, bạn cần có mã nhúng ở phần 1. Mã nhúng sẽ giúp định danh khách hàng kể cả khi họ không
                  đăng nhập.{' '}
                </Text>

                <Text as="h3" variant="headingSm">
                  Dành cho mobile app
                </Text>
                <Text as="p">
                  Bạn thực hiện truy vấn như trên phiên bản web, tuy nhiên yêu cầu người dùng đăng nhập vì trên mobile app không sử dụng được thư viện
                  Passport.js
                </Text>
              </BlockStack>
            </Card>
          </InlineGrid>

          {smUp ? <Divider /> : null}

          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Maps key
                </Text>
                <Text as="p" variant="bodyMd">
                  Key của dịch vụ Maps(goong.io)
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <BlockStack gap={'400'}>
                <TextField
                  label="Key kết nối"
                  autoComplete="off"
                  {...fields._maps_key}
                  helpText={`Key này dùng để kết nối các dịch vụ gợi ý điểm đón.`}
                />
                <TextField
                  label="URI kết nối"
                  autoComplete="off"
                  {...fields._maps_uri}
                  helpText={`URI này dùng để kết nối tới dịch vụ, ví dụ: https://api.banhang.xuanquynh.vn/goong/v2.`}
                />
              </BlockStack>
            </Card>
          </InlineGrid>

          {smUp ? <Divider /> : null}

          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Mắt bão Invoice API
                </Text>
                <Text as="p" variant="bodyMd">
                  Kết nối ứng dụng của bạn với dịch vụ hóa đơn của Mắt Bão
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <Form onSubmit={submit}>
                <BlockStack gap={'200'}>
                  <Text as="h3" variant="headingSm">
                    Mắt bão Invoice API Connection
                  </Text>
                  <TextField label="Mã số thuế" autoComplete="off" {...fields._matbao_mst} />
                  <FormLayout.Group>
                    <TextField label="Tài khoản" autoComplete="off" {...fields._matbao_taikhoan} />
                    <TextField type="password" showCharacterCount label="Khóa bí mật/mật khẩu" autoComplete="off" {...fields._matbao_matkhau} />
                  </FormLayout.Group>

                  <FormLayout.Group condensed>
                    <TextField label="_matbao_khmshdon" autoComplete="off" {...fields._matbao_khmshdon} />
                    <TextField label="_matbao_khhdon" autoComplete="off" {...fields._matbao_khhdon} />
                    <TextField label="Thuế suất *** %?" autoComplete="off" {...fields._matbao_thuesuat} />
                  </FormLayout.Group>
                  <TextField label="API URI" autoComplete="off" {...fields._matbao_api_uri} />

                  <Checkbox
                    label="Tự động tạo hóa đơn nháp sau 23h hàng ngày."
                    checked={useFields.__auto_create_draft_invoice_before_midnight.value === '1'}
                    onChange={() =>
                      useFields.__auto_create_draft_invoice_before_midnight.onChange(
                        useFields.__auto_create_draft_invoice_before_midnight.value === '0' ? '1' : '0'
                      )
                    }
                    helpText="Trước 23h00, hệ thống kiểm tra các đơn hàng thỏa mãn điều kiện: Đã hoàn thành, chuyến đã chốt lệnh, đã thanh toán, sẽ được tạo hóa đơn tự động."
                  />

                  <Checkbox
                    label="Tự động ký hóa đơn nháp trước 23h30 đêm."
                    checked={useFields._auto_sign_invoice_before_midnight.value === '1'}
                    onChange={() =>
                      useFields._auto_sign_invoice_before_midnight.onChange(useFields._auto_sign_invoice_before_midnight.value === '0' ? '1' : '0')
                    }
                    helpText="Với các hóa đơn nháp, hệ thống sẽ tự thay mặt bạn ký chúng trước kết thúc ngày (GMT+7)."
                  />
                </BlockStack>
              </Form>
            </Card>
          </InlineGrid>

          {smUp ? <Divider /> : null}

          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  MapBox
                </Text>
                <Text as="p" variant="bodyMd">
                  Key của dịch vụ MapBox, để vẽ bản đồ 3D và chỉ đường.
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <BlockStack gap={'400'}>
                <TextField
                  label="Private Key của MapBox"
                  autoComplete="off"
                  {...fields._mapbox_key}
                  helpText={`Key này dùng để vẽ bản đồ 3D và chỉ đường.`}
                />
                <TextField
                  label="Public Key của MapBox"
                  autoComplete="off"
                  {...fields._mapbox_public_key}
                  helpText={`Key này dùng để vẽ bản đồ 3D và chỉ đường.`}
                />
              </BlockStack>
            </Card>
          </InlineGrid>

          {/*
          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  ------
                </Text>
                <Text as="p" variant="bodyMd">
                  ----- ----- ----
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">------</Card>
          </InlineGrid> */}

          {/** END */}
        </BlockStack>

        <br />
        <br />
        <br />
        <br />
      </Page>

      {dirty && (
        <ContextualSaveBar
          message="Thay đổi chưa được lưu"
          saveAction={{ onAction: () => submit(), loading: updating }}
          discardAction={{ onAction: () => resetForm() }}
        />
      )}
    </>
  );
}
